import { v4 as uuidV4 } from "uuid";
import { parseISO, isDate, format } from "date-fns";
import { useSelector } from "react-redux";
import {
  getRecord,
  getAllRecords,
  putRecord,
  deleteRecord,
  getKeys,
} from "../idb";

import * as XLXS from "xlsx";
import { parseCsvText, toCsvText } from "../utils/csvUtils";

import useCryptography from "./useCyptography";
import useErrorTracking from "./useErrorTracking";

import useApi from "./useApi";
import usePage from "./usePage";
import useDebug from "./useDebug";

const useData = () => {
  const { encrypt, decrypt, generateDailyPasscode } = useCryptography();
  const { getData, postData } = useApi();
  const { showMessage } = usePage();
  const { consoleLog } = useDebug();
  const { logDataError } = useErrorTracking();

  const email = useSelector((state) => state.authentication.email);

  const generateColumnsFromSchema = (entitySchema) => {
    const cols = [];

    const fieldNames = Object.keys(entitySchema);
    for (const fieldName of fieldNames) {
      if (entitySchema[fieldName].showInList) {
        cols.push({
          name: fieldName,
          colIndex: entitySchema[fieldName].showInList,
        });
      }
    }
    return sort(cols, "colIndex");
  };

  const sort = (array, field, order = "asc", parse = (x) => x) => {
    const sortOrder = order === "asc" ? 1 : -1;
    let sortedArray = [...array];

    if (field) {
      sortedArray.sort((a, b) => {
        let result = 0;
        if (parse(a[field] < parse(b[field]))) {
          result = -1;
        }
        if (parse(a[field] > parse(b[field]))) {
          result = 1;
        }
        return result * sortOrder;
      });
    } else {
      if (order === "asc") {
        sortedArray.sort((a, b) => (a < b ? -1 : 1));
      } else {
        sortedArray.reverse();
      }
    }

    return sortedArray;
  };

  const fieldSorter = (fields) => (a, b) =>
    fields
      .map((o) => {
        let dir = 1;
        if (o[0] === "-") {
          dir = -1;
          o = o.substring(1);
        }
        let left = a[o] ? a[o] : " ";
        let right = b[o] ? b[o] : " ";
        return left > right ? dir : left < right ? -dir : 0;
      })
      .reduce((p, n) => (p ? p : n), 0);

  const sortByFields = (array, fields) => {
    // Normalize inputs so callers that get null/undefined don't crash sort
    const sortable = Array.isArray(array) ? array : [];
    const sortFields = Array.isArray(fields) ? fields : [];

    if (sortable.length === 0 || sortFields.length === 0) return [...sortable];

    const sortedArray = [...sortable];
    sortedArray.sort(fieldSorter(sortFields));

    return sortedArray;
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const newId = () => {
    return uuidV4();
  };

  const putRecordInIDB = async (storeName, key, data) => {
    if (!data) return;

    const stringData = JSON.stringify(data);
    const encryptedData = encrypt(stringData);
    await putRecord(storeName, key, encryptedData);
  };

  const getRecordFromIDB = async (storeName, key) => {
    const encryptedData = await getRecordFromIDB(storeName, key);

    if (!encryptedData) return null;

    const decryptedData = decrypt(encryptedData);
    const data = JSON.parse(decryptedData);
    return data;
  };

  const deleteRecordFromIDB = async (storeName, key) => {
    await deleteRecord(storeName, key);
  };

  const getAllFromIDB = async (storeName) => {
    const records = await getAllRecords(storeName);
    return records;
  };

  const getAllKeysFromIDB = async (storeName) => {
    const keys = await getKeys(storeName);
    return keys;
  };

  const toDate = (value) => {
    if (!value) return null;

    if (isDate(value)) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const isoValue = parseISO(trimmed);
      if (!Number.isNaN(isoValue.getTime())) return isoValue;

      const fallbackValue = new Date(trimmed);
      return Number.isNaN(fallbackValue.getTime()) ? null : fallbackValue;
    }

    if (typeof value === "number") {
      const numericDate = new Date(value);
      return Number.isNaN(numericDate.getTime()) ? null : numericDate;
    }

    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  const timeToDate = (date, time) => {
    let newDatetime = time;
    newDatetime.setYear(date.getFullYear());
    newDatetime.setMonth(date.getMonth());
    newDatetime.setDate(date.getDate());
    return newDatetime;
  };

  const toNumber = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") return parseInt(value);
  };

  const isISODateString = (value) => {
    const isoDateFormat =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

    return value && typeof value === "string" && isoDateFormat.test(value);
  };

  const urlBase64ToUint8Array = (base64String) => {
    var padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    var base64 = (base64String + padding)
      .replace(/\-/g, "+")
      .replace(/_/g, "/");

    var rawData = window.atob(base64);
    var outputArray = new Uint8Array(rawData.length);

    for (var i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const decodeBase64 = (encoded) => {
    return new Uint8Array(
      atob(encoded)
        .split("")
        .map((c) => c.charCodeAt(0)),
    );
  };

  const decode = (input) => {
    try {
      return decodeBase64(
        input.replace(/-/g, "+").replace(/_/g, "/").replace(/\s/g, ""),
      );
    } catch (_a) {
      throw new TypeError("The input to be decoded is not correctly encoded.");
    }
  };

  const excelToJson = (file, cb) => {
    if (!file) return cb(null);

    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target.result;

      const workbook = XLXS.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLXS.utils.sheet_to_json(sheet);
      cb(jsonData);
    };

    reader.readAsArrayBuffer(file);
  };

  const csvToJson = (file, cb) => {
    if (!file) return cb(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e?.target?.result || "";
      const results = parseCsvText(text, {
        header: true,
        skipEmptyLines: true,
      });
      cb(results.data);
    };
    reader.readAsText(file);
  };

  const jsonToCSV = (json) => {
    if (!json) return null;

    const csv = toCsvText(json);

    return csv;
  };

  const getFromServer = async ({ entityName, pipeline, id, filter }) => {
    try {
      const parms = {
        entityName,
        pipeline: pipeline ? pipeline : "standard",
      };

      if (id) {
        parms.id = id;
      } else if (filter) {
        parms.filter = filter;
      }

      const results = await getData(parms);

      if (results.isSuccessful) {
        return results.data;
      }
    } catch (err) {
      await logDataError(err, "useData.getFromServer", {
        entityName,
        pipeline,
        id,
        hasFilter: !!filter,
      });
      consoleLog("useData.getFromServer", err.message, err);
    }
    return null;
  };

  const getListFromServer = async (entityName, filter) => {
    const parms = {
      entityName,
      action: "get",
      filter,
      pipeline: "list",
    };

    const results = await postData(parms);

    if (results.isSuccessful) {
      return results.data;
    }

    return null;
  };

  const executeAction = async ({
    entityName,
    action,
    id,
    data,
    filter,
    options,
    pageNumber,
    recordsPerPage,
    pipeline,
    orderBy,
    order,
    displayMessage,
    customMessage,
  }) => {
    // console.log({ data });
    const results = await postData({
      entityName: entityName,
      action: action,
      id,
      data: data,
      filter,
      options,
      pageNumber,
      recordsPerPage,
      pipeline,
      orderBy,
      order,
    });

    if (displayMessage) {
      if (customMessage) {
        showMessage(customMessage);
      } else {
        showMessage(results.message);
      }
    }

    // Always return the full envelope: { isSuccessful, data, message, ... }
    // Never unwrap arrays - let callers handle the structure consistently
    return results || null;
  };

  const updateRecord = async (entityName, data, displayMessage = true) => {
    let action = "add";
    if (data && data.id) action = "update";

    const results = await postData({
      entityName: entityName,
      action: action,
      data: data,
    });

    if (displayMessage) {
      if (results.isSuccessful) {
        showMessage("saved");
      } else {
        showMessage(results.message);
      }
    }

    return results;
  };

  const deleteRecord = async (entityName, id) => {
    const results = await postData({
      entityName: entityName,
      action: "delete",
      id,
    });

    if (results.isSuccessful) {
      showMessage("deleted");
    } else {
      showMessage(results.message);
    }

    return results;
  };

  const deleteMany = async (entityName, filter) => {
    const results = await postData({
      entityName: entityName,
      action: "delete",
      filter,
    });

    if (results.isSuccessful) {
      showMessage("deleted");
    } else {
      showMessage(results.message);
    }
  };

  const resultsToOptions = (data) => {
    const options = [];

    for (const item of data) {
      options.push({ id: item.id, name: item.name });
    }

    return options;
  };

  const extractQuestions = (survey) => {
    let questions = [];

    if (Array.isArray(survey.pages)) {
      for (let pageIndex = 0; pageIndex < survey.pages.length; pageIndex++) {
        const page = survey.pages[pageIndex];

        if (Array.isArray(page.sections)) {
          for (
            let sectionIndex = 0;
            sectionIndex < page.sections.length;
            sectionIndex++
          ) {
            const section = page.sections[sectionIndex];

            if (Array.isArray(section.questions)) {
              for (
                let questionIndex = 0;
                questionIndex < section.questions.length;
                questionIndex++
              ) {
                const question = section.questions[questionIndex];

                questions.push({
                  surveyId: survey._id,
                  pageIndex,
                  sectionIndex,
                  questionIndex,
                  ...question,
                });
              }
            }
          }
        }
      }
    }

    return questions;
  };

  const extractControlQuestions = (
    survey,
    forPageIndex,
    forSectionIndex,
    forQuestionIndex,
    forLevel,
  ) => {
    let questions = [];

    if (Array.isArray(survey.pages)) {
      for (let pageIndex = 0; pageIndex < survey.pages.length; pageIndex++) {
        if (pageIndex >= forPageIndex) continue;

        const page = survey.pages[pageIndex];

        if (Array.isArray(page.sections)) {
          for (
            let sectionIndex = 0;
            sectionIndex < page.sections.length;
            sectionIndex++
          ) {
            if (pageIndex === forPageIndex && sectionIndex < forSectionIndex) {
              continue;
            }

            const section = page.sections[sectionIndex];

            if (section.sectionType === "text") continue;

            if (forLevel === "section") {
              if (forPageIndex !== null && section.sectionType !== "question")
                continue;

              if (
                forSectionIndex !== null &&
                section.sectionType !== "question"
              )
                continue;
            }

            if (Array.isArray(section.questions)) {
              for (
                let questionIndex = 0;
                questionIndex < section.questions.length;
                questionIndex++
              ) {
                const question = section.questions[questionIndex];

                if (
                  !["radio", "checkbox", "select", "multiselect"].includes(
                    question.questionType,
                  )
                ) {
                  continue;
                }

                if (questionIndex === forQuestionIndex) continue;

                if (forLevel === "question") {
                  if (
                    section.sectionType === "product" ||
                    section.sectionType === "matrix"
                  ) {
                    if (sectionIndex !== forSectionIndex) {
                      continue;
                    }
                  }
                }

                questions.push({
                  surveyId: survey._id,
                  pageIndex,
                  sectionIndex,
                  questionIndex,
                  ...question,
                });
              }
            }
          }
        }
      }
    }

    return questions;
  };

  const addToWorkQueue = async (workType, details) => {
    const recipientEmail = details?.email || email;

    const results = await postData({
      entityName: "WorkQueue",
      action: "add",
      data: {
        workType,
        details: {
          ...details,
          email: recipientEmail,
        },
      },
    });

    return results;
  };

  const runReport = async ({ entityName, reportName, filter }) => {
    const results = await postData({
      entityName: entityName,
      action: reportName,
      data: { filter },
    });

    return results;
  };

  const findAll = (array, filter) => {
    if (!Array.isArray(array)) return [];
    if (!filter || Object.keys(filter).length === 0) return array;

    const results = array.filter((item) =>
      Object.entries(filter).every(([key, value]) => item[key] === value),
    );

    return results;
  };

  const findOne = (array, filter) => {
    if (!Array.isArray(array)) return [];
    if (!filter || Object.keys(filter).length === 0) return array;

    const results = array.find((item) =>
      Object.entries(filter).every(([key, value]) => item[key] === value),
    );

    return results ? results : null;
  };

  const todaysPasscode = () => {
    return generateDailyPasscode();
  };

  const isDateBetween = (targetDateStr, startDateStr, endDateStr) => {
    const target = new Date(targetDateStr);
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);

    // Normalize time to midnight for accurate comparison
    target.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return target >= start && target <= end;
  };

  const hasValue = (array1, checkValue) => {
    const checkFor = Array.isArray(checkValue) ? checkValue : [checkValue];
    return checkFor.some((value) => array1.includes(value));
  };

  return {
    sort,
    sortByFields,
    newId,
    reorder,
    encrypt,
    decrypt,
    putRecordInIDB,
    getRecordFromIDB,
    deleteRecordFromIDB,
    getAllFromIDB,
    getAllKeysFromIDB,
    toDate,
    toNumber,
    isISODateString,
    urlBase64ToUint8Array,
    decode,
    generateColumnsFromSchema,
    getFromServer,
    resultsToOptions,
    extractQuestions,
    extractControlQuestions,
    addToWorkQueue,
    updateRecord,
    deleteRecord,
    getListFromServer,
    executeAction,
    deleteMany,
    excelToJson,
    csvToJson,
    runReport,
    jsonToCSV,
    timeToDate,
    findAll,
    findOne,
    todaysPasscode,
    isDateBetween,
    hasValue,
  };
};

export default useData;
